import { DataTable } from "@/components/ui/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { Mail, Trash2, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { useMediaQuery } from "@/lib/hooks/use-media-query";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

type Contact = {
  id: string;
  fullName: string;
  email: string;
  message: string;
  createdAt: string;
};

interface ContactTableProps {
  data: Contact[];
  onDelete: (contact: Contact) => void;
  onEmail: (email: string) => void;
  onView: (contact: Contact) => void;
}

export function ContactTable({ data, onDelete, onEmail, onView }: ContactTableProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Handle case where data might be passed as an object with contacts array or as the array itself
  const contacts = Array.isArray(data) ? data : (data as any).contacts || [];

  const columns: ColumnDef<Contact>[] = [
    {
      accessorKey: "fullName",
      header: "Name",
      cell: ({ row }) => (
        <span className="text-muted-foreground">{row.getValue("fullName")}</span>
      ),
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => (
        <div className="flex items-center text-foreground">
          <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
          {row.getValue("email")}
        </div>
      ),
    },
    {
      accessorKey: "message",
      header: "Message",
      cell: ({ row }) => (
        <Button
          variant="ghost"
          className="p-0 h-auto hover:bg-transparent"
          onClick={() => onView(row.original)}
        >
          <p className="truncate max-w-md text-muted-foreground hover:text-primary">
            {row.getValue("message")}
          </p>
        </Button>
      ),
    },
    {
      accessorKey: "createdAt",
      header: "Date",
      cell: ({ row }) =>
        <span className="text-foreground">{format(new Date(row.getValue("createdAt") as string), "MMM d, yyyy HH:mm")}</span>,
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <div className="flex justify-end space-x-2 text-muted-foreground">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onView(row.original)}
            title="View message"
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEmail(row.original.email)}
            title="Send email"
          >
            <Mail className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(row.original)}
            title="Delete message"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  if (isMobile) {
    if (contacts.length === 0) {
      return <div className="text-center p-8 text-muted-foreground">No messages found</div>;
    }

    return (
      <div className="space-y-4">
        {contacts.map((contact: Contact) => (
          <Card key={contact.id} className="bg-card">
            <CardHeader className="p-4 pb-2">
              <div className="flex justify-between items-start">
                <CardTitle className="text-base font-semibold text-foreground">{contact.fullName}</CardTitle>
                <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                  {format(new Date(contact.createdAt), "MMM d, HH:mm")}
                </span>
              </div>
            </CardHeader>
            <CardContent className="p-4 py-2 space-y-3">
              <div className="flex items-center text-sm text-muted-foreground">
                <Mail className="h-3 w-3 mr-2 shrink-0" />
                <span className="truncate">{contact.email}</span>
              </div>
              <div
                className="bg-muted/30 p-3 rounded-md cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => onView(contact)}
              >
                <p className="text-sm text-foreground line-clamp-3">
                  {contact.message}
                </p>
              </div>
            </CardContent>
            <CardFooter className="p-4 pt-2 flex justify-end gap-1 border-t border-border/50">
              <Button variant="ghost" size="sm" onClick={() => onView(contact)}>
                <Eye className="h-4 w-4 mr-2" />
                View
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onEmail(contact.email)}>
                <Mail className="h-4 w-4 mr-2" />
                Reply
              </Button>
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => onDelete(contact)}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>
    );
  }

  return <DataTable columns={columns} data={contacts} searchKey="fullName" />;
}